// =============================================================================
// useLivingConversation: the shared voice-client brain (thin SDK wrapper)
//
// EXTRACTED from apps/web/src/lib/useLivingConversation.ts (COPY-then-adapt,
// the Get Found lock pattern — apps/web's original is UNTOUCHED). This is the
// theme-agnostic, app-agnostic core: any app on the same careeropt-api backend
// can mount <ConversationProvider> + call this hook and get the same session
// lifecycle apps/web's corpus canvas already proves in production.
//
// WHAT CHANGED FROM THE ORIGINAL (deliberate, not oversight — read before
// diverging further):
//   - `startAgentSession` is no longer imported directly. The original lived
//     in apps/web/src/lib/api.ts, which this package must not depend on (that
//     would couple every consumer to apps/web's whole API surface + create a
//     workspace cycle risk). Callers now pass a `mintSession: MintSession`
//     function bound to their OWN apiFetch — apps/throughline's version wraps
//     its own `apiFetch('/agent-session', ...)`, identical wire contract.
//   - `traceStudio` (apps/web/src/lib/errorLog.ts) is replaced by an optional
//     `onTrace` callback. The original's tracing was fire-and-forget debug
//     logging tied to apps/web's error-reporting pipe; a generic package
//     should not assume any particular app's logging story. Passing nothing
//     is a safe no-op (matches the original's own graceful-degradation ethos).
//   - GAP (honest, not silently dropped): the RESILIENCE layer — RovingCap
//     (session-cap rollover/grace/ceiling) + studioDeviceCache (IndexedDB
//     turn buffer surviving a drop/reload) + the dev-only barge-in spike —
//     is NOT ported. That stack is corpus-canvas-specific (STUDIO_RESILIENCE
//     flag, tied to the enrichment loop's device cache) and is a candidate
//     for its OWN extraction pass once a second app needs it, not bundled
//     into the B-2 orb scope. A live session here still connects, speaks,
//     listens, and ends cleanly — it simply has no session-cap rollover or
//     offline turn buffer yet. Flagged in the brief's gaps list.
//   - `coerceRehomeCard` / `coerceLandFacet` are ported verbatim (pure
//     functions, zero apps/web coupling) since they are part of the frozen
//     client-tool CONTRACT (agent dashboard tool names must match), not
//     app-specific logic.
//
// TRANSPORT (unchanged from the original, verified against @elevenlabs/react
// @1.6.4 / @elevenlabs/client@1.9.0):
//   - WebRTC path: conversation.startSession({ conversationToken,
//     connectionType: 'webrtc', overrides, dynamicVariables, customLlmExtraBody,
//     userId }). startSession is SYNCHRONOUS (returns void); the bound
//     conversationId arrives via onConnect, not a Promise.
//   - getInputByteFrequencyData / getOutputByteFrequencyData return a
//     Uint8Array of the live voice spectrum, for the orb visualiser.
//   - onMessage receives { message, role: 'user' | 'agent', source } per
//     finalised line, for a read-only caption ribbon.
//
// IMPORTANT: `useConversation` REQUIRES a <ConversationProvider> ancestor in
// @elevenlabs/react v1+. The consuming app must mount it (apps/throughline's
// App.tsx does, mirroring apps/web's own hoist-to-root pattern).
//
// GRACEFUL DEGRADATION: never throws to the render. Reports failure via
// `status === 'disconnected'` + the onError callback, same as the original.
// =============================================================================

import { type Language, useConversation } from '@elevenlabs/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  AgentSessionResponse,
  DnaFacetType,
  FocusCardPayload,
  LandFacetPayload,
  MintSession,
  RehomeCardPayload,
} from './types';
import { CLIENT_TOOL_NAMES } from './types';

/** The conversation lifecycle status, re-exported from the SDK's vocabulary so
 *  callers do not import the SDK directly. */
export type LivingStatus = 'disconnected' | 'connecting' | 'connected' | 'disconnecting';

/** Who is talking right now. Drives the orb tint (warm = speaking, cool =
 *  listening) and which caption (agent words vs the user's transcript). */
export type LivingMode = 'speaking' | 'listening';

/** A snapshot of the two frequency arrays for the listening-orb visualiser.
 *  Each is a Uint8Array of byte magnitudes (0-255) over the voice spectrum.
 *  Empty arrays when no conversation is active (the SDK returns empty, never
 *  throws). */
export interface LivingFrequencies {
  input: Uint8Array;
  output: Uint8Array;
}

/** The handlers the caller supplies so the hook can drive the UI as the loop
 *  fires. All optional: a bare voice session works without any of them. */
export interface LivingConversationHandlers {
  /** A facet landed in-flight (the landFacet tool). */
  onFacet?: (facet: LandFacetPayload) => void;
  /** The agent PROPOSED a re-home mid-conversation (the rehomeCard tool). This
   *  handler only RECEIVES the proposal — it does not write; the caller's own
   *  confirm UI + API call does the gated re-home. */
  onRehomeProposal?: (proposal: RehomeCardPayload) => void;
  /** The agent or user said something: drive a read-only caption ribbon. */
  onCaption?: (caption: { source: 'agent' | 'user'; text: string; isFinal: boolean }) => void;
  /** A typed transport error. */
  onError?: (message: string) => void;
  /** Optional debug trace sink (replaces the original's traceStudio import —
   *  see the module header). Never required; omit for silent operation. */
  onTrace?: (event: string, detail?: Record<string, unknown>) => void;
}

/** What the caller hands `start()` to ground the session on a card/role. */
export interface LivingStartOptions {
  anchorType?: 'achievement' | 'role' | 'origins' | 'gap';
  anchorId?: string | null;
  /** Reuse an existing session rather than minting a new one. */
  sessionId?: string;
  gap?: { text: string; jobId: string };
}

/** The frozen return shape: { status, mode, isSpeaking, frequencies, start,
 *  stop } plus the client-tool registration (handled internally) and a
 *  couple of steering helpers (focusCard, the live sessionId). */
export interface UseLivingConversation {
  status: LivingStatus;
  mode: LivingMode;
  isSpeaking: boolean;
  /** Reads the current input/output byte-frequency data. Call inside a rAF
   *  loop in the visualiser; cheap and safe when disconnected (returns
   *  empty). */
  frequencies: () => LivingFrequencies;
  /** Mint a token (via the caller's mintSession) and open the WebRTC session,
   *  grounded on the given anchor. Resolves to the bound session id, or null
   *  on failure (caller degrades gracefully). Never throws to the render. */
  start: (options?: LivingStartOptions) => Promise<string | null>;
  /** End the session and release the mic. */
  stop: () => void;
  /** Steer the agent to a card the user tapped (the focusCard direction). */
  focusCard: (payload: FocusCardPayload) => void;
  /** The bound session id, once started. */
  sessionId: string | null;
}

// The five DNA facet kinds, used to defensively coerce an inbound tool-call
// param into a known facetType (never trust the wire shape blindly).
const FACET_TYPES: ReadonlySet<string> = new Set<DnaFacetType>([
  'accomplishment',
  'knowledge',
  'lesson',
  'thread',
  'driver',
]);

/** Coerce a loose record (the raw client-tool params from the agent) into a
 *  well-formed LandFacetPayload. Returns null only when there is nothing to
 *  show (no content at all). Ported verbatim from the original. */
export function coerceLandFacet(params: Record<string, unknown>): LandFacetPayload | null {
  const content = typeof params.content === 'string' ? params.content.trim() : '';
  if (!content) return null;

  const rawType = typeof params.facetType === 'string' ? params.facetType : '';
  const facetType: DnaFacetType = FACET_TYPES.has(rawType)
    ? (rawType as DnaFacetType)
    : 'accomplishment';

  const normalRaw = params.isNormalToMe;
  const isNormalToMe =
    normalRaw === true || normalRaw === 1 || normalRaw === '1' || normalRaw === 'true';

  const facetId =
    typeof params.facetId === 'string' && params.facetId.length > 0
      ? params.facetId
      : `live-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

  const targetRoleId =
    typeof params.targetRoleId === 'string' && params.targetRoleId.length > 0
      ? params.targetRoleId
      : null;
  const targetAchievementId =
    typeof params.targetAchievementId === 'string' && params.targetAchievementId.length > 0
      ? params.targetAchievementId
      : null;
  const originLabel =
    typeof params.originLabel === 'string' && params.originLabel.length > 0
      ? params.originLabel
      : null;

  return {
    facetId,
    facetType,
    content,
    isNormalToMe,
    targetRoleId,
    targetAchievementId,
    originLabel,
  };
}

/** Coerce the raw `rehomeCard` client-tool params into a well-formed
 *  RehomeCardPayload. A proposal is only surfaced when it carries the card it
 *  is about AND a concrete destination; a vague correction with no target is
 *  dropped rather than surfaced as an un-actionable chip. Ported verbatim. */
export function coerceRehomeCard(params: Record<string, unknown>): RehomeCardPayload | null {
  const achievementId =
    typeof params.achievementId === 'string' && params.achievementId.length > 0
      ? params.achievementId
      : '';
  if (!achievementId) return null;

  const nested =
    params.proposedTarget && typeof params.proposedTarget === 'object'
      ? (params.proposedTarget as Record<string, unknown>)
      : {};
  const pickStr = (nestedVal: unknown, flatVal: unknown): string | null => {
    if (typeof nestedVal === 'string' && nestedVal.length > 0) return nestedVal;
    if (typeof flatVal === 'string' && flatVal.length > 0) return flatVal;
    return null;
  };
  const targetRoleId = pickStr(nested.roleId, params.proposedRoleId);
  const targetRoleEventId = pickStr(nested.roleEventId, params.proposedRoleEventId);
  if (!targetRoleId && !targetRoleEventId) return null;

  const rawLabel = pickStr(nested.label, params.proposedLabel);
  const label = rawLabel && rawLabel.trim().length > 0 ? rawLabel.trim() : 'the corrected era';
  const spokenCorrection =
    typeof params.spokenCorrection === 'string' ? params.spokenCorrection.trim() : '';

  return {
    achievementId,
    achievementHeadline:
      typeof params.achievementHeadline === 'string' ? params.achievementHeadline.trim() : '',
    proposedTarget: { roleId: targetRoleId, roleEventId: targetRoleEventId, label },
    spokenCorrection,
  };
}

/** The SDK's overrides.agent.language is a narrow language-code union, while
 *  AgentSessionResponse types it as a plain string (the backend owns the
 *  actual code). Narrow the one field at the boundary so the rest of the
 *  overrides object passes through verbatim. */
function toSdkOverrides(overrides: AgentSessionResponse['overrides']) {
  const agent = overrides.agent;
  return {
    ...overrides,
    agent: agent
      ? {
          ...agent,
          language: agent.language as Language | undefined,
        }
      : undefined,
  };
}

/**
 * The shared voice hook. Wires the real @elevenlabs/react `useConversation`:
 * registers the three client tools, maps onMessage to the caption callback,
 * exposes status/mode/isSpeaking + the frequency getters, mints the token via
 * the caller-supplied `mintSession` and opens the WebRTC session, and returns
 * null from start() on any failure (graceful degradation). Never throws to
 * the render.
 */
export function useLivingConversation(
  mintSession: MintSession,
  handlers: LivingConversationHandlers = {},
): UseLivingConversation {
  const [sessionId, setSessionId] = useState<string | null>(null);
  const sessionIdRef = useRef<string | null>(null);
  // One start at a time — the in-flight promise dedupes re-clicks/re-mounts
  // during the silent mint window (the original's fix for the "two Miras"
  // defect: concurrent mints both connecting).
  const startInFlightRef = useRef<Promise<string | null> | null>(null);

  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  const trace = useCallback((event: string, detail?: Record<string, unknown>) => {
    handlersRef.current.onTrace?.(event, detail);
  }, []);

  const clientTools = {
    [CLIENT_TOOL_NAMES.landFacet]: (params: Record<string, unknown>): void => {
      const facet = coerceLandFacet(params ?? {});
      if (facet) handlersRef.current.onFacet?.(facet);
    },
    [CLIENT_TOOL_NAMES.focusCard]: (_params: Record<string, unknown>): void => {
      // Agent-initiated focus is rare; the usual direction is client -> agent
      // via focusCard() below. No-op so the tool name is registered and a
      // stray agent-side call does not surface as an unhandled-tool error.
      void _params;
    },
    [CLIENT_TOOL_NAMES.rehomeCard]: (params: Record<string, unknown>): void => {
      const proposal = coerceRehomeCard(params ?? {});
      if (proposal) handlersRef.current.onRehomeProposal?.(proposal);
    },
  };

  const conversation = useConversation({
    clientTools,
    onConnect: (props: { conversationId?: string }) => {
      trace('connected', { conversationId: props?.conversationId ?? null });
    },
    onDisconnect: (details?: unknown) => {
      trace('disconnected', { details: summariseUnknown(details) });
    },
    onMessage: (props: { message: string; role: 'user' | 'agent'; source?: string }) => {
      const text = typeof props?.message === 'string' ? props.message : '';
      if (!text) return;
      trace('message', { role: props.role, chars: text.length });
      const source: 'agent' | 'user' = props.role === 'user' ? 'user' : 'agent';
      handlersRef.current.onCaption?.({ source, text, isFinal: true });
    },
    onError: (message: unknown) => {
      trace('sdk-error', {
        message: typeof message === 'string' ? message.slice(0, 300) : summariseUnknown(message),
      });
      handlersRef.current.onError?.(typeof message === 'string' ? message : 'voice error');
    },
  });

  // Trace status transitions (disconnected -> connecting -> connected ...).
  const prevStatusRef = useRef<string>('disconnected');
  useEffect(() => {
    const s = conversation.status as string;
    if (s !== prevStatusRef.current) {
      trace('status', { from: prevStatusRef.current, to: s });
      prevStatusRef.current = s;
    }
  }, [conversation.status, trace]);

  const start = useCallback(
    async (options: LivingStartOptions = {}): Promise<string | null> => {
      if (startInFlightRef.current) {
        trace('start-deduped', { reason: 'in_flight' });
        return startInFlightRef.current;
      }
      const liveStatus = conversation.status as string;
      if (liveStatus === 'connected' || liveStatus === 'connecting') {
        trace('start-deduped', { reason: liveStatus });
        return sessionIdRef.current;
      }
      const run = (async (): Promise<string | null> => {
        trace('start', {
          anchorType: options.anchorType ?? 'origins',
          anchorId: options.anchorId ?? null,
        });
        try {
          const session = await mintSession({
            anchorType: options.anchorType,
            anchorId: options.anchorId ?? null,
            sessionId: options.sessionId,
            gap: options.gap,
          });
          sessionIdRef.current = session.sessionId;
          setSessionId(session.sessionId);
          trace('token-minted', { sessionId: session.sessionId, agentId: session.agentId });

          trace('start-session', { connectionType: 'webrtc' });
          conversation.startSession({
            conversationToken: session.conversationToken,
            connectionType: 'webrtc',
            overrides: toSdkOverrides(session.overrides),
            dynamicVariables: session.dynamicVariables,
            customLlmExtraBody: { sessionId: session.sessionId, userId: session.userId },
            onConnect: ({ conversationId }: { conversationId: string }) => {
              trace('start-session-connected', { conversationId });
            },
            onError: (message: string) => {
              trace('start-session-error', {
                message: (message || 'voice connect failed').slice(0, 300),
              });
              handlersRef.current.onError?.(message || 'voice connect failed');
            },
          });

          return session.sessionId;
        } catch (err) {
          const msg = err instanceof Error ? err.message : 'session start failed';
          trace('start-failed', { message: msg.slice(0, 300) });
          handlersRef.current.onError?.(msg);
          sessionIdRef.current = null;
          setSessionId(null);
          return null;
        }
      })();
      startInFlightRef.current = run;
      try {
        return await run;
      } finally {
        startInFlightRef.current = null;
      }
      // conversation.startSession is stable across renders (the SDK memoises
      // it); referencing conversation directly keeps the closure current
      // without re-creating start on every render.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [conversation, mintSession, trace],
  );

  const stop = useCallback(() => {
    conversation.endSession();
    sessionIdRef.current = null;
    setSessionId(null);
  }, [conversation]);

  const focusCard = useCallback(
    (payload: FocusCardPayload) => {
      const target =
        payload.anchorType === 'origins'
          ? 'their origins'
          : `${payload.anchorType} ${payload.anchorId ?? '(unspecified)'}`;
      conversation.sendContextualUpdate(
        `The person just tapped ${target} on the canvas. Pivot to it: ask about that next, building on what they have already said.`,
      );
    },
    [conversation],
  );

  const frequencies = useCallback((): LivingFrequencies => {
    return {
      input: conversation.getInputByteFrequencyData(),
      output: conversation.getOutputByteFrequencyData(),
    };
  }, [conversation]);

  return {
    status: conversation.status as LivingStatus,
    mode: conversation.mode as LivingMode,
    isSpeaking: conversation.isSpeaking,
    frequencies,
    start,
    stop,
    focusCard,
    sessionId,
  };
}

/** Best-effort, truncated stringify of an unknown SDK payload, so a trace
 *  never throws and never floods the log. */
function summariseUnknown(value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'string') return value.slice(0, 300);
  try {
    return JSON.stringify(value).slice(0, 300);
  } catch {
    return String(value).slice(0, 300);
  }
}
